import { Dialog, Button, formikHelpers, toast } from '@round/creatorbase-ui';
import { FormikProvider, useFormik } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import { creatorbase, creatorbaseQueries } from '@round/api';
import { useCreatePoolMutation } from '@round/creatorbase-helpers/api-hooks';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { mapApiErrorsToFormikErrors } from 'utility/utility';
import styles from './CreatePoolModal.module.css';
import * as yup from 'yup';

const { Field, InputField, TextareaField } = formikHelpers;
const createUtils = creatorbaseQueries.pools.utils.create;

const validationSchema = yup.object({
    name: yup.string().trim().required('Name is required'),
    description: yup.string(),
});

type FormValues = {
    name: string;
    description: string;
};

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (pool: creatorbase.Pool) => void;
};

export const CreatePoolModal = ({ isOpen, onClose, onCreated }: Props) => {
    const { selectedClientId } = useClientSelect();
    const queryClient = useQueryClient();
    const { create } = useCreatePoolMutation(queryClient);

    const formik = useFormik<FormValues>({
        initialValues: { name: '', description: '' },
        validationSchema,
        validateOnMount: true,
        onSubmit: (values, helpers) => {
            if (typeof selectedClientId !== 'number') {
                helpers.setSubmitting(false);
                return;
            }

            create(
                {
                    client_id: selectedClientId,
                    name: values.name.trim(),
                    description: values.description.trim() || undefined,
                },
                {
                    onSuccess: (pool) => {
                        toast.info('Pool created');
                        onCreated(pool);
                        onClose();
                        helpers.resetForm();
                    },
                    onError: (e) => {
                        helpers.setSubmitting(false);

                        if (createUtils.isExpectedErrorResponse(e) && e.response.status === 400) {
                            helpers.setErrors(mapApiErrorsToFormikErrors(e.response.data));
                            return;
                        }

                        toast.error('Could not create pool');
                    },
                }
            );
        },
    });

    const handleClose = () => {
        if (formik.isSubmitting) {
            return;
        }

        formik.resetForm();
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <Dialog.Content>
                <Dialog.Header>
                    <Dialog.Title>Create pool</Dialog.Title>
                </Dialog.Header>

                <FormikProvider value={formik}>
                    <Dialog.Body className={styles.body}>
                        <Field name="name">
                            <Field.Label htmlFor="name">Name</Field.Label>
                            <InputField name="name" placeholder="Enter pool name" disabled={formik.isSubmitting} />
                            <Field.Error name="name" />
                        </Field>

                        <Field name="description">
                            <Field.Label htmlFor="description">Description</Field.Label>
                            <TextareaField
                                name="description"
                                placeholder="Enter pool description"
                                disabled={formik.isSubmitting}
                            />
                            <Field.Error name="description" />
                        </Field>
                    </Dialog.Body>
                </FormikProvider>

                <Dialog.Footer>
                    <Button variant="outline" size="sm" onClick={handleClose} disabled={formik.isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="default"
                        size="sm"
                        onClick={formik.submitForm}
                        disabled={!formik.isValid || formik.isSubmitting}
                        isLoading={formik.isSubmitting}
                    >
                        Create pool
                    </Button>
                </Dialog.Footer>
            </Dialog.Content>
        </Dialog>
    );
};
